import {
  Box,
  Button,
  Flex,
  Icon,
  IconButton,
  Menu,
  MenuButton,
  MenuList,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  useDisclosure,
} from "@chakra-ui/react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { SingleCandidateBlock } from "../../components/CandidateBlock/SingleCandidateBlock/SingleCandidateBlock";
import { CandidateInfo } from "../../components/CandidateBlock/CandidateInfo/CandidateInfo";
import { Layout } from "../../components/Layout/Layout";
import { useAppDispatch, useAppSelector } from "../../store";
import { useEffect } from "react";
import {
  fetchCandidateBlacklist,
  fetchDeleteCandidate,
  fetchSingleCandidate,
} from "../../store/slices/candidates";
import PageLoaderWrapper from "../../components/PageLoaderWrapper";
import { ArrowBackIcon } from "@chakra-ui/icons";
import { ChakraTabs } from "../../components/tabs/ChakraTabs";
import { formatDate } from "../../helpers/formatDate";
import { FiClock, FiEdit } from "react-icons/fi";

export const SingleCandidatePage = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const {
    isOpen: isBlacklistOpen,
    onOpen: onBlacklistOpen,
    onClose: onBlacklistClose,
  } = useDisclosure();
  const { currentCandidate, isLoading } = useAppSelector(
    (state) => state.candidates
  );

  // Управление активным табом
  const getActiveTabIndex = () => {
    const tab = searchParams.get("tab");
    switch (tab) {
      case "vacancies":
        return 1;
      case "details":
      default:
        return 0;
    }
  };

  const activeTabIndex = getActiveTabIndex();

  useEffect(() => {
    dispatch(fetchSingleCandidate(Number(id)));
  }, [id, dispatch]);

  const handleDeleteCandidate = () => {
    if (id) {
      dispatch(fetchDeleteCandidate(Number(id)))
        .then((res: any) => {
          if (res.payload === 1) {
            navigate("/candidate");
          }
        })
        .catch((error) => {
          console.error("Error deleting candidate:", error);
        });
    }
  };

  const handleBlacklistAction = (action: "add" | "remove") => {
    if (id) {
      dispatch(fetchCandidateBlacklist({ id: Number(id), action }))
        .then(() => {
          dispatch(fetchSingleCandidate(Number(id)));
          onBlacklistClose();
        })
        .catch((error) => {
          console.error("Error updating blacklist status:", error);
        });
    }
  };

  const isBlacklisted = currentCandidate?.status?.name === "Черный список";

  return (
    <Layout>
      <PageLoaderWrapper
        isLoading={isLoading}
        loadingText="Загрузка данных кандидата..."
      >
        <Flex
          flexDirection="column"
          gap="24px"
          paddingRight="32px"
          maxH="90vh"
          overflowY="auto"
        >
          <Text
            color="gray.500"
            cursor="pointer"
            onClick={() => navigate("/candidate")}
            display="flex"
            alignItems="center"
            gap="8px"
          >
            <ArrowBackIcon />К списку кандидатов
          </Text>
          <Flex alignItems="flex-start" justifyContent="space-between">
            <Flex alignItems="flex-start" gap="8px">
              <Flex flexDirection="column" gap="4px">
                <Text
                  fontSize={30}
                  lineHeight="36px"
                  fontWeight={700}
                  color="#3b515e"
                >
                  {currentCandidate?.last_name ?? ""}{" "}
                  {currentCandidate?.first_name ?? ""}{" "}
                  {currentCandidate?.patronymic_name ?? ""}
                </Text>
                <Flex alignItems="center" gap="8px">
                  <Text
                    fontSize={16}
                    lineHeight="24px"
                    fontWeight={400}
                    color="#65758b"
                  >
                    {currentCandidate?.expected_position ?? ""}
                  </Text>
                </Flex>
              </Flex>
            </Flex>
            <Flex gap="16px">
              <Button
                leftIcon={<Icon as={FiEdit as any} boxSize={4} />}
                onClick={() => navigate("edit")}
              >
                Редактировать
              </Button>
              <Button
                colorScheme={isBlacklisted ? "green" : "red"}
                variant="outline"
                onClick={onBlacklistOpen}
              >
                {isBlacklisted ? "Убрать из ЧС" : "Добавить в ЧС"}
              </Button>
              <Button colorScheme="red" variant="outline" onClick={onOpen}>
                Удалить
              </Button>
              <Menu>
                <MenuButton
                  leftIcon={<Icon as={FiClock as any} boxSize={4} />}
                  as={Button}
                  variant="outline"
                >
                  История
                </MenuButton>
                <MenuList maxH="400px" overflowY="auto" w="400px">
                  {currentCandidate?.activities?.length ? (
                    currentCandidate.activities.map((activity, idx) => (
                      <Box key={idx} p="12px" borderBottom="1px solid #e2e8f0">
                        <Text fontWeight={600} fontSize="sm" marginBottom="4px">
                          {formatDate(activity.created_at, true)}
                        </Text>
                        <Text fontSize="sm" color="gray.600" whiteSpace="pre-line">
                          {activity.action}
                        </Text>
                      </Box>
                    ))
                  ) : (
                    <Box p="12px">
                      <Text fontSize="sm" color="gray.500">
                        История изменений пуста
                      </Text>
                    </Box>
                  )}
                </MenuList>
              </Menu>
            </Flex>
          </Flex>
          <Flex flexDirection="column" gap="32px">
            <Flex flexDirection="column" gap="16px" flex={1} minW={0}>
              <Tabs index={activeTabIndex} isLazy>
                <Box w="500px">
                  <ChakraTabs
                    tabs={[
                      { id: "details", label: "Детали" },
                      { id: "vacancies", label: "Вакансии" },
                    ]}
                    baseUrl={`/candidate/${id}`}
                  />
                </Box>
                <TabPanels paddingTop="16px">
                  <TabPanel paddingX={0} paddingTop={0}>
                    <Flex flexDirection="column" gap="16px">
                      <Box>
                        <SingleCandidateBlock />
                      </Box>
                    </Flex>
                  </TabPanel>
                  <TabPanel paddingX={0} paddingTop={0}>
                    <CandidateInfo />
                  </TabPanel>
                </TabPanels>
              </Tabs>
            </Flex>
          </Flex>
        </Flex>
      </PageLoaderWrapper>

      <Modal isOpen={isOpen} onClose={onClose} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Удаление кандидата</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text>
              Вы уверены, что хотите удалить кандидата? Это действие необратимо.
            </Text>
          </ModalBody>
          <ModalFooter gap="16px">
            <Button variant="ghost" onClick={onClose}>
              Отмена
            </Button>
            <Button
              colorScheme="red"
              onClick={() => {
                handleDeleteCandidate();
                onClose();
              }}
            >
              Удалить
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Modal isOpen={isBlacklistOpen} onClose={onBlacklistClose} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            {isBlacklisted
              ? "Удаление из черного списка"
              : "Добавление в черный список"}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text>
              {isBlacklisted
                ? "Вы уверены, что хотите убрать кандидата из черного списка?"
                : "Вы уверены, что хотите добавить кандидата в черный список?"}
            </Text>
          </ModalBody>
          <ModalFooter gap="16px">
            <Button variant="ghost" onClick={onBlacklistClose}>
              Отмена
            </Button>
            <Button
              colorScheme={isBlacklisted ? "green" : "red"}
              onClick={() => {
                handleBlacklistAction(isBlacklisted ? "remove" : "add");
              }}
            >
              {isBlacklisted ? "Убрать из ЧС" : "Добавить в ЧС"}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Layout>
  );
};
